import { useEffect, useState } from 'react';

import { CMS_API_URL } from '../config/apiBase';

// Process-wide cache with in-flight dedup (same pattern as useBrandSettings).
// The schools list is mounted many times per page (Header, Footer,
// SchoolSelector, visibility/navigation hooks) — without this every instance
// fired its own identical request. Unlike brand settings, schools carry shop
// open/close windows, so entries expire after a short TTL instead of living
// for the whole session.
const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map();    // brandSlug -> { schools, at }
const inflight = new Map(); // brandSlug -> Promise<schools>

function readCache(brandSlug) {
  const entry = cache.get(brandSlug);
  if (!entry) return null;
  if (Date.now() - entry.at > CACHE_TTL_MS) {
    cache.delete(brandSlug);
    return null;
  }
  return entry.schools;
}

function load(brandSlug) {
  const cached = readCache(brandSlug);
  if (cached) return Promise.resolve(cached);
  if (inflight.has(brandSlug)) return inflight.get(brandSlug);

  const p = fetch(`${CMS_API_URL}/api/schools/${encodeURIComponent(brandSlug)}`)
    .then(async (res) => {
      if (!res.ok) throw new Error(`Schools API returned ${res.status}`);
      const json = await res.json();
      cache.set(brandSlug, { schools: json, at: Date.now() });
      return json;
    })
    // Errors are NOT cached — the next mount retries.
    .finally(() => inflight.delete(brandSlug));

  inflight.set(brandSlug, p);
  return p;
}

/**
 * Fetch all active schools (with classes and shop dates) for a brand.
 * Deduped process-wide: concurrent mounts share one request, results are
 * cached for a few minutes.
 *
 * @param {string} brandSlug — brand identifier (e.g. 'clsdk')
 * @returns {{ schools: Array, loading: boolean, error: Error|null }}
 *
 * Each school: { id, name, city, classes: string[], shopOpensAt, shopClosesAt }
 */
export default function useSchools(brandSlug) {
  const [state, setState] = useState(() => {
    const cached = brandSlug ? readCache(brandSlug) : null;
    return {
      schools: cached ?? [],
      loading: Boolean(brandSlug) && !cached,
      error: null,
    };
  });

  useEffect(() => {
    let cancelled = false;

    if (!brandSlug) {
      setState({ schools: [], loading: false, error: null });
      return () => { cancelled = true; };
    }

    const cached = readCache(brandSlug);
    if (cached) {
      setState({ schools: cached, loading: false, error: null });
      return () => { cancelled = true; };
    }

    setState((s) => ({ ...s, loading: true, error: null }));
    load(brandSlug)
      .then((schools) => {
        if (!cancelled) setState({ schools, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(`[useSchools] Failed to fetch schools for "${brandSlug}":`, err);
          setState({ schools: [], loading: false, error: err });
        }
      });

    return () => { cancelled = true; };
  }, [brandSlug]);

  return state;
}
